import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import RootTask from '../../../Task/Form/RootTask';

import { SUB_TASK_COLUMNS } from 'utils/DataTransform/Task/subTask';
import {
  TASK_STATUS,
  SUBTASK_DEFAULT,
  TASK_DEFAULT,
} from 'utils/constants/task';
import { createError } from 'reducers/ui';
import API from 'services/api';
import { errorHandler } from 'utils/errorMessages';
import FormDataPopulation from 'components/Forms/Fields/FormDataPopulation';
import { validateRequired, validationObject } from 'utils/validations';
import { TASK_FIELDS } from 'utils/formFields/task';
import { SCHEDULE_PATTERNS } from 'utils/constants/schedule';
import { cloneTaskInitialValues } from '../../../Task/Form/options';
import { convertPercentageToimageCordinates } from 'utils/DataTransform/CleanerTask/CleanerTaskDetails';
import { v4 as uuidv4 } from 'uuid';
import { t } from 'i18next';
import { getTimezone } from 'reducers/utils';
import { actions as templateActions } from './Modules/dataGridTable';
import * as templateSelectors from './Selectors/templateSelectors';
import { PAGE_NAME } from 'utils/constants';
import isEmpty from 'lodash/isEmpty';

const CleaningForm = ({
  id,
  editMode,
  closeForm,
  updateTemplateTask,
  createTemplateTask,
  templateTask,
  createError,
  addCleaningTaskToEventModel,
  editModeValues,
  isTemplate = true,
  fromEvents,
  formTitle,
  taskFilters = [],
  multiSchedule = true,
  timezone,
  disablePresist = false,
  businessClients = [],
  sites = [],
  rawClients,
  users,
}) => {
  const defaultValues = {
    isTemplate: true,
    wrapsId: null,
    scanCode: null,
    scanAutoStart: null,
    scanStartOnly: null,
    surfaceViewPreferred: true,
    status: TASK_STATUS.NEW.id,
    ...(taskFilters &&
      taskFilters.length === 1 && { taskType: taskFilters[0].id }),
  };

  const [resetValues, setResetValues] = useState();

  const handleCloseForm = () => {
    if (typeof addCleaningTaskToEventModel === 'function') {
      return;
    }
    closeForm();
    // setFormProps({});
  };

  const createTask = async ({ data, rootTaskId }) => {
    //instead of sending api request add it to the event model state
    if (typeof addCleaningTaskToEventModel === 'function') {
      closeForm();
      addCleaningTaskToEventModel({
        data,
        rootTaskId,
        sendToServer: true, // the data is now saved in the state and no need to call the api to get the data on edit
      });
      return;
    }
    await createTemplateTask({ data, rootTaskId, fromEvents });
    if (fromEvents) {
      closeForm();
    }
    handleCloseForm();
  };
  const onEdit = async ({ data, rootTaskId }) => {
    //instead of sending api request add it to the event model state
    if (typeof addCleaningTaskToEventModel === 'function') {
      closeForm();
      addCleaningTaskToEventModel({
        data,
        rootTaskId,
        sendToServer: true, // the data is now saved in the state and no need to call the api to get the data on edit
      });
      return;
    }
    await updateTemplateTask({ data, rootTaskId });
    handleCloseForm();
  };

  const onChangeCallback = async ({ values, details }) => {
    try {
      const { data } = isEmpty(details)
        ? await API.fetchTaskDetails({
            id: values.copyFromTemplate.id,
          })
        : details;

      // eslint-disable-next-line prefer-const
      let { tasks, joins, surfaces } = data.data;
      // change the coordinates back to x and y from percentages
      joins = convertPercentageToimageCordinates(joins, surfaces);
      tasks = convertPercentageToimageCordinates(tasks, surfaces);

      // eslint-disable-next-line no-unused-vars
      const { id, ...newFormInitial } = cloneTaskInitialValues({
        taskToCloneId: values.copyFromTemplate.id,
        taskToCloneDetails: { tasks, joins, surfaces },
        defaultValues,
        subTaskDefaultValues: SUBTASK_DEFAULT,
        allowSubTask: true,
        siteList: sites,
      });
      //when copying we need to prefix with the correct language version of Copy
      const taskNamePrefix = t('Copy');
      // change scanCode on root task when copying from template
      const setScanCodeValues = {
        ...newFormInitial,
        scanCode:
          newFormInitial?.scanCode !== null
            ? uuidv4()
            : newFormInitial.scanCode,
        scanStartOnly: newFormInitial.scanStartOnly,
        taskName: `${taskNamePrefix} - ${newFormInitial.taskName}`,
      };
      setResetValues(setScanCodeValues);
    } catch (e) {
      createError(errorHandler(e));
    }
  };
  // set a default value of the taskType in case there is only one type i.e cleaning
  const defaultFormValues = !isTemplate
    ? {
        ...TASK_DEFAULT,
        isTemplate,
        ...(taskFilters &&
          taskFilters.length === 1 && { taskType: taskFilters[0].id }),
      }
    : {
        ...TASK_DEFAULT,
        ...(taskFilters &&
          taskFilters.length === 1 && { taskType: taskFilters[0].id }),
      };

  const subTaskDefaultformValues = {
    ...SUBTASK_DEFAULT,
    ...(taskFilters &&
      taskFilters.length === 1 && { taskType: taskFilters[0].id }),
  };

  return (
    <RootTask
      hideAssignee
      allowSubTask
      multiSchedule={multiSchedule}
      formTitle={formTitle}
      id={id || editModeValues?.rootTaskId}
      editMode={editMode}
      closeForm={handleCloseForm}
      defaultValues={{
        ...defaultFormValues,
        timezone,
      }}
      resetValues={resetValues}
      setResetValues={setResetValues}
      createTask={createTask}
      editTask={onEdit}
      subtaskColumns={Object.values(SUB_TASK_COLUMNS)}
      taskDetails={templateTask || editModeValues}
      allowedTaskType={taskFilters}
      subtaskDefaultValues={subTaskDefaultformValues}
      schedulingView={[SCHEDULE_PATTERNS.onceOff.id]}
      addCleaningTaskToEventModel={addCleaningTaskToEventModel}
      persistentStateName={PAGE_NAME.TEMPLATE_TASKS}
      disablePresist={disablePresist}
      businessClients={businessClients}
      sites={sites}
      rawClients={rawClients}
      users={users}
      {...(!editMode && {
        headerContent: (
          <FormDataPopulation
            formHeader="Select Template to Copy"
            buttonText="Copy Existing Template"
            submitCallback={onChangeCallback}
            subFields={[TASK_FIELDS.template({ taskType: '' })]}
            validationSchema={validationObject({
              copyFromTemplate: validateRequired().nullable(),
            })}
            initialValues={{ copyFromTemplate: null }}
          />
        ),
      })}
    />
  );
};

CleaningForm.propTypes = {
  id: PropTypes.string,
  formTitle: PropTypes.string,
  editMode: PropTypes.bool,
  isTemplate: PropTypes.bool,
  fromEvents: PropTypes.bool,
  closeForm: PropTypes.func.isRequired,
  createTemplateTask: PropTypes.func,
  updateTemplateTask: PropTypes.func,
  templateTask: PropTypes.object,
  setFormProps: PropTypes.func,
  createError: PropTypes.func,
  addCleaningTaskToEventModel: PropTypes.func,
  editModeValues: PropTypes.object,
  allowedTaskTypes: PropTypes.array,
  taskFilters: PropTypes.array,
  multiSchedule: PropTypes.bool,
  disablePresist: PropTypes.bool,
  timezone: PropTypes.string,
  businessClients: PropTypes.array,
  sites: PropTypes.array,
  rawClients: PropTypes.array,
  users: PropTypes.object,
};

const mapStateToProps = state => ({
  templateTask: templateSelectors.getDetailsTransformedRaw(state),
  timezone: getTimezone(state),
});

const mapDispatchToProps = dispatch => ({
  createTemplateTask: ({ data, rootTaskId, fromEvents }) =>
    dispatch(templateActions.createRowEntry({ data, rootTaskId, fromEvents })),
  updateTemplateTask: ({ id, data, rootTaskId }) =>
    dispatch(templateActions.updateRowEntry({ id, data, rootTaskId })),
  createError: err => dispatch(createError(err)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CleaningForm);
